/**
 * Values from plan.md §9 that both the client and the server must agree on.
 * Anything only one side needs stays in that side's config.
 */
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;
export const OPTION_TEXT_MAX = 200;
export const DISPLAY_NAME_MAX = 20;
export const AVATAR_SEED_MAX = 64;
/** Setup runs two minutes per decision. plan.md §1.2. */
export const SETUP_MS_PER_DECISION = 2 * 60 * 1000;
export const TURN_WINDOW_MS = 30 * 1000;
export const REVEAL_WINDOW_MS = 30 * 1000;
/**
 * How long the results screen stays up before returning to the lobby on its own.
 *
 * plan.md §1.10 describes this as a maintenance-mode behaviour; it runs
 * unconditionally instead, because an abandoned results screen otherwise holds
 * a room until its two-hour TTL whether or not anyone is deploying. Five
 * minutes rather than two: this is the part of the evening people actually
 * enjoy, and a screenshot and an argument about the scores should not be cut
 * short to serve an operational convenience. See plan.md §5.13.
 */
export const RESULTS_WINDOW_MS = 5 * 60 * 1000;
/** How many decisions each player writes, by player count. plan.md §1.2. */
export const decisionsPerPlayer = (playerCount) => {
    if (playerCount <= 2)
        return 3;
    if (playerCount <= 4)
        return 2;
    return 1;
};
export const setupDurationMs = (playerCount) => decisionsPerPlayer(playerCount) * SETUP_MS_PER_DECISION;
/**
 * Two sets of five, picked by how the reveal went for the player looking at it.
 * A reveal is either a small victory or a small humiliation, and the row of
 * emoji should already agree with you before you tap anything.
 *
 * Five, not six: one row of comfortable tap targets across a phone, with no
 * wrapping and no crowding at the ends.
 */
export const CELEBRATION_EMOJI = ["🐐", "🎉", "😂", "🔥", "👏"];
export const DISAPPOINTMENT_EMOJI = ["💩", "💀", "🤮", "😱", "🫠"];
/**
 * Everything the server will accept. Which half a client is *offered* is a
 * presentation decision — the server does not know, or need to know, whether
 * the sender guessed right, so it validates against the union.
 */
export const REACTION_EMOJI = [
    ...CELEBRATION_EMOJI,
    ...DISAPPOINTMENT_EMOJI,
];
/**
 * Results is not a win or a loss for everyone at once — the same screen is
 * being read by whoever came first and whoever came last — so it mixes both
 * sets rather than picking one. Every member is in `REACTION_EMOJI`, so the
 * server needs no separate rule for this screen.
 */
export const RESULTS_EMOJI = ["🎉", "🐐", "😂", "💀", "💩"];
/**
 * Spamming reactions is the point, not an abuse to be throttled — this is the
 * one moment the whole table is reacting to the same thing at once. The limit
 * exists only so a script cannot flood the room channel.
 *
 * Note that `COMMAND_RATE_LIMIT` in `roomSocket` used to bind first at 30 per
 * 10s across *all* commands, so no reaction limit above ~3/s was reachable.
 * React is now exempt from that counter and bounded here instead.
 */
export const REACTION_RATE_LIMIT = 40;
export const REACTION_RATE_WINDOW_MS = 5_000;
